import {css} from 'aphrodite';
import React from 'react';

import * as FileConstants from 'folder-items/FileConstants';
import BenchLink from 'link-preview/BenchLink';
import TagValueTypedValueViewer, {
  styles,
} from 'notebook/tables/typed-values/components/viewers/TagValueTypedValueViewer';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import * as TagUtil from 'schemas/tags/TagUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import UrlAwareText from 'ui-library/UrlAwareText';
import isValidLink from 'util/isValidLink';

/**
 * When a "summarize" property (like above) is true, only actually summarize when the chip count >= this.
 */
export const SUMMARIZE_THRESHOLD_DEFAULT = 11;

/**
 * Component for viewing a field value based on a particular field type.
 */

interface TagValueViewerProps {
  chipSize?: ChipSize;
  shouldPreviewImages?: boolean;
  showChipsAsLinks?: boolean;
  tag: FileConstants.Tag | FileConstants.MinimalSchemaTag;
  summarize?: boolean;
  summarizeThreshold?: number;
  shouldCollapseBlobs?: boolean;
  showSummaryChipsAsPopover?: boolean;
  shouldOpenLinkInNewTab?: boolean;
}

/**
 * Component for rendering Schema and custom Tags.
 */
export default function TagValueViewer({
  chipSize = ChipSize.SMALL,
  shouldPreviewImages = true,
  showChipsAsLinks = true,
  tag,
  summarize = false,
  summarizeThreshold = SUMMARIZE_THRESHOLD_DEFAULT,
  shouldCollapseBlobs = true,
  showSummaryChipsAsPopover,
  shouldOpenLinkInNewTab,
}: TagValueViewerProps): JSX.Element | null {
  const potentialUrl = tag.url || tag.value;
  if (isValidLink(potentialUrl)) {
    if (shouldOpenLinkInNewTab) {
      return (
        <a href={potentialUrl!} target="_blank" rel="noreferrer">
          {tag.value}
        </a>
      );
    } else {
      return <BenchLink href={potentialUrl!}>{tag.value}</BenchLink>;
    }
  }
  if (FileConstants.isSchemaTag(tag)) {
    const placeholder = TagUtil.getTagPlaceholder(tag);
    return (
      <TagValueTypedValueViewer
        chipSize={chipSize}
        shouldPreviewImages={shouldPreviewImages}
        showChipsAsLinks={showChipsAsLinks}
        summarize={summarize}
        summarizeThreshold={summarizeThreshold}
        typedValue={convertTagToTypedValue(tag)}
        textValue={tag.value || ''}
        schemaFieldRenderData={{
          name: tag.tagSchemaField.name,
          fieldType: tag.tagSchemaField.fieldType,
          unitSymbol: tag.tagSchemaField.unitSymbol,
        }}
        placeholder={placeholder}
        shouldShowDateAndDatetimesAsChips={false}
        shouldCollapseBlobs={shouldCollapseBlobs}
        showSummaryChipsAsPopover={showSummaryChipsAsPopover}
      />
    );
  } else {
    return <UrlAwareText textValue={tag.value} className={css(styles.text)} />;
  }
}
